import { createElement, useEffect, useState } from "react";
import { Platform, Pressable, StyleSheet, Switch, Text, View } from "react-native";
import { detectWebViewMode } from "../../utils/webViewMode";
import {
  clearHlsCache,
  applyAutomaticCacheCapacity,
  getHlsCacheSettings,
  getHlsCacheStats,
  HlsCacheSettings as CacheSettings,
  HlsCacheStats,
  HlsCacheStrategy,
  saveHlsCacheSettings,
  getDesktopCachePolicy,
  saveDesktopCachePolicy,
} from "../../services/mobileHlsCache";

const EMPTY_STATS: HlsCacheStats = { playlists: 0, segments: 0, bytes: 0 };
const strategyLabels: Record<HlsCacheStrategy, string> = {
  watched: "仅缓存已播放（最省空间）",
  balanced: "平衡：已播放 + 前后预缓存",
  aggressive: "积极：提前缓存更多内容",
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export default function HlsCacheSettings() {
  const [settings, setSettings] = useState<CacheSettings>(getHlsCacheSettings);
  const [stats, setStats] = useState<HlsCacheStats>(EMPTY_STATS);
  const [message, setMessage] = useState("");
  const [storageInfo, setStorageInfo] = useState({ usage: 0, quota: 0 });
  const desktop = Platform.OS === "web" && detectWebViewMode() === "desktop";
  const [profile, setProfile] = useState(getDesktopCachePolicy);
  const [capacityGB, setCapacityGB] = useState(String(profile.policy.capacityGB));
  const [desktopEnabled, setDesktopEnabled] = useState(profile.policy.enabled);

  const refreshStats = () =>
    getHlsCacheStats()
      .then(setStats)
      .catch((error) => setMessage(String(error?.message || error)));

  useEffect(() => {
    refreshStats();
    navigator.storage?.estimate?.().then((estimate) =>
      setStorageInfo({ usage: estimate.usage || 0, quota: estimate.quota || 0 }),
    );
  }, []);

  useEffect(() => {
    if (!desktop) return;
    const changed = () => {
      const next = getDesktopCachePolicy();
      setProfile(next);
      setSettings(getHlsCacheSettings());
      setCapacityGB(String(next.policy.capacityGB));
      setDesktopEnabled(next.policy.enabled);
    };
    window.addEventListener("playback-cache-policy-changed", changed);
    return () => window.removeEventListener("playback-cache-policy-changed", changed);
  }, [desktop]);

  const update = (patch: Partial<CacheSettings>) => {
    const next = saveHlsCacheSettings({ ...settings, ...patch });
    setSettings(next);
    setMessage("缓存设置已保存到当前设备");
  };

  if (Platform.OS !== "web") return null;

  if (desktop) return (
    <View style={styles.card}>
      <Text style={styles.title}>电脑版 · 本机媒体缓存</Text>
      <Text style={styles.help}>已成功缓存的切片和视频分块优先复用，不依赖播放进度，不按时间过期；超出所设容量或浏览器配额时仍可能被清理。</Text>
      <Text style={styles.help}>当前{profile.accountId ? "账号设置" : "本机设置"}：{profile.policy.enabled ? "开启" : "关闭"} · {profile.policy.capacityGB === 0 ? "不设网站容量上限" : `${profile.policy.capacityGB} GB`}；本设备有效上限 {settings.maxCacheBytes === Number.MAX_SAFE_INTEGER ? "由浏览器决定" : formatBytes(settings.maxCacheBytes)}。</Text>
      <Switch value={desktopEnabled} onValueChange={setDesktopEnabled} />
      <Text style={styles.label}>缓存容量上限（GB，0 表示不设上限）</Text>
      {createElement("input", { type: "number", min: 0, step: 0.25,
        value: capacityGB, style: styles.select as never,
        onChange: (event: { target: { value: string } }) => setCapacityGB(event.target.value) })}
      <Text style={styles.help}>输入 0 不设网站容量上限，或输入不小于 0.25 的容量；实际受浏览器配额限制。{profile.accountId ? "保存后同步至账号。" : "保存到当前浏览器；当前不提供账号同步。"}</Text>
      <Pressable style={styles.secondaryButton} onPress={() => {
        const size = Number(capacityGB);
        if (!capacityGB.trim() || !Number.isFinite(size) || (size !== 0 && size < 0.25)) { setMessage("请输入 0（不设上限）或不小于 0.25 GB 的容量"); return; }
        saveDesktopCachePolicy({ ...profile.policy, enabled: desktopEnabled, capacityGB: size }).then(() => setMessage(profile.accountId ? "账号缓存设置已保存" : "缓存设置已保存到当前浏览器")).catch(error => setMessage(error.message));
      }}><Text>保存缓存设置</Text></Pressable>
      <Pressable style={styles.secondaryButton} onPress={() => {
        saveDesktopCachePolicy(null).then(() => {
          const next = getDesktopCachePolicy(); setCapacityGB(String(next.policy.capacityGB)); setDesktopEnabled(next.policy.enabled); setMessage("已恢复默认缓存设置");
        }).catch(error => setMessage(error.message));
      }}><Text>恢复默认</Text></Pressable>
      <Text style={styles.stats}>当前缓存：{stats.segments} 个切片 / {stats.videos || 0} 个视频文件或分块 / {formatBytes(stats.bytes)}</Text>
      <Pressable style={styles.secondaryButton} onPress={refreshStats}><Text>刷新缓存统计</Text></Pressable>
      <Pressable style={styles.dangerButton} onPress={() => clearHlsCache().then(setStats).catch(error => setMessage(error.message))}><Text>清空当前账号缓存</Text></Pressable>
      <Text style={styles.help}>需要 HTTPS 或 localhost。若浏览器不支持缓存、空间不足或源站未允许分段读取，将继续在线播放。</Text>
      {message ? <Text style={styles.message}>{message}</Text> : null}
    </View>
  );

  return (
    <View style={styles.card}>
      <View style={styles.titleRow}>
        <View style={styles.titleCopy}>
          <Text style={styles.title}>视频缓存与续播</Text>
          <Text style={styles.subtitle}>手机和电脑版浏览器使用同一套本机缓存规则</Text>
        </View>
        <Switch value={settings.enabled} onValueChange={(enabled) => update({ enabled })} />
      </View>

      <View style={styles.field}>
        <Text style={styles.label}>容量分配方式</Text>
        {createElement(
          "select",
          {
            value: settings.capacityMode,
            onChange: (event: { target: { value: "auto" | "manual" } }) => {
              const capacityMode = event.target.value;
              if (capacityMode === "auto") {
                applyAutomaticCacheCapacity({ ...settings, capacityMode }).then(setSettings);
                setMessage("已按当前设备剩余容量自动分配");
              } else {
                update({ capacityMode });
              }
            },
            style: styles.select as never,
            disabled: !settings.enabled,
          },
          [
            ["auto", "自动：使用约 90% 可用空间（无固定上限）"],
            ["manual", "手动：由用户指定上限"],
          ].map(([value, label]) => createElement("option", { key: value, value }, label)),
        )}
        <Text style={styles.help}>
          浏览器已用 {formatBytes(storageInfo.usage)} / 可用配额 {formatBytes(storageInfo.quota)}
        </Text>
      </View>

      <View style={styles.field}>
        <Text style={styles.label}>缓存容量上限</Text>
        {createElement(
          "select",
          {
            value: String(settings.maxCacheBytes),
            onChange: (event: { target: { value: string } }) =>
              update({
                capacityMode: "manual",
                maxCacheBytes: Number(event.target.value),
                maxSegments: 5000,
              }),
            style: styles.select as never,
            disabled: !settings.enabled || settings.capacityMode === "auto",
          },
          [
            ...(settings.capacityMode === "auto"
              ? [[settings.maxCacheBytes / 1024 / 1024, `自动 ${formatBytes(settings.maxCacheBytes)}`] as [number, string]]
              : []),
            [50, "50 MB"],
            [100, "100 MB"],
            [250, "250 MB"],
            [500, "500 MB"],
            [1024, "1 GB"],
            [2048, "2 GB"],
            [4096, "4 GB"],
            [8192, "8 GB"],
            [16384, "16 GB"],
            [32768, "32 GB"],
            [65536, "64 GB"],
          ].map(([mb, label]) =>
            createElement("option", { key: String(mb), value: Number(mb) * 1024 * 1024 }, label),
          ),
        )}
        {settings.capacityMode === "auto" && (
          <Text style={styles.help}>当前自动上限：{formatBytes(settings.maxCacheBytes)}</Text>
        )}
      </View>

      <View style={styles.field}>
        <Text style={styles.label}>缓存策略</Text>
        {createElement(
          "select",
          {
            value: settings.strategy,
            onChange: (event: { target: { value: HlsCacheStrategy } }) => {
              const strategy = event.target.value;
              update({
                strategy,
                preloadSegments: strategy === "watched" ? 0 : strategy === "aggressive" ? 12 : 3,
              });
            },
            style: styles.select as never,
            disabled: !settings.enabled,
          },
          (Object.keys(strategyLabels) as HlsCacheStrategy[]).map((value) =>
            createElement("option", { key: value, value }, strategyLabels[value]),
          ),
        )}
      </View>

      <View style={styles.toggleRow}>
        <View style={styles.titleCopy}>
          <Text style={styles.label}>电脑版也缓存</Text>
          <Text style={styles.help}>关闭后，/pc/ 只在线播放，不占本机缓存</Text>
        </View>
        <Switch
          value={settings.desktopEnabled}
          disabled={!settings.enabled}
          onValueChange={(desktopEnabled) => update({ desktopEnabled })}
        />
      </View>
      <View style={styles.toggleRow}>
        <View style={styles.titleCopy}>
          <Text style={styles.label}>记住播放位置</Text>
          <Text style={styles.help}>重新打开视频时提示是否从上次位置继续</Text>
        </View>
        <Switch
          value={settings.resumePlayback}
          onValueChange={(resumePlayback) => update({ resumePlayback })}
        />
      </View>

      <View style={styles.statsRow}>
        <Text style={styles.stats}>
          当前缓存：{stats.segments} 个切片 / {formatBytes(stats.bytes)}
        </Text>
        <Pressable style={styles.secondaryButton} onPress={refreshStats}>
          <Text style={styles.secondaryButtonText}>刷新统计</Text>
        </Pressable>
        <Pressable
          style={styles.dangerButton}
          onPress={() =>
            clearHlsCache()
              .then((next) => {
                setStats(next);
                setMessage("视频切片缓存已清空，续播位置仍保留");
              })
              .catch((error) => setMessage(String(error?.message || error)))
          }
        >
          <Text style={styles.dangerButtonText}>清空缓存</Text>
        </Pressable>
      </View>
      {message ? <Text style={styles.message}>{message}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 14,
    marginVertical: 12,
    padding: 16,
    borderRadius: 12,
    backgroundColor: "#f7f7f8",
    borderWidth: 1,
    borderColor: "#e2e2e5",
    gap: 14,
  },
  titleRow: { flexDirection: "row", alignItems: "center", gap: 12 },
  titleCopy: { flex: 1, minWidth: 0 },
  title: { fontSize: 18, fontWeight: "800", color: "#151515" },
  subtitle: { fontSize: 12, lineHeight: 18, color: "#666", marginTop: 3 },
  field: { gap: 6 },
  label: { fontSize: 14, fontWeight: "700", color: "#222" },
  help: { fontSize: 12, color: "#777", marginTop: 3 },
  select: {
    width: "100%",
    height: 38,
    paddingLeft: 10,
    border: "1px solid #c9c9cc",
    borderRadius: 7,
    backgroundColor: "white",
    color: "#222",
  } as never,
  toggleRow: { flexDirection: "row", alignItems: "center", gap: 12 },
  statsRow: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 8 },
  stats: { flex: 1, minWidth: 180, fontSize: 13, color: "#444" },
  secondaryButton: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 7, backgroundColor: "#e5e5e8" },
  secondaryButtonText: { color: "#222", fontSize: 13, fontWeight: "700" },
  dangerButton: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 7, backgroundColor: "#ffe4e4" },
  dangerButtonText: { color: "#b51e1e", fontSize: 13, fontWeight: "700" },
  message: { fontSize: 12, color: "#2f7d42" },
});
